import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Zap, ShieldCheck, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
    usePageMeta({
        title: "Contact — Quantum Genesis Labs Research Peptides",
        description: "Contact Quantum Genesis Labs for research inquiries, product information, or general support. Premium research peptides — laboratory and research use only.",
        path: "/contact",
    });
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSuccess(true);
            toast.success("Message sent — our team will reply within 1 business day.");
            setForm({ name: "", email: "", subject: "", message: "", company: "" });
        } catch (err) {
            console.error(err);
            toast.error("Could not send message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20" data-testid="contact-page">
            <header className="max-w-3xl mb-14">
                <p className="text-xs tracking-[0.25em] uppercase text-indigo-400 mb-3">Contact</p>
                <h1 className="text-4xl md:text-5xl font-light tracking-tighter">
                    Get in <span className="text-gradient">touch.</span>
                </h1>
                <p className="mt-4 text-slate-400 leading-relaxed">
                    Reach out directly for research inquiries, product information, or general support.
                </p>
            </header>

            {/* ─── PRIMARY: Telegram support card ───────────────────────────── */}
            <section
                data-testid="telegram-primary-card"
                className="relative overflow-hidden rounded-3xl border border-indigo-500/25 bg-gradient-to-br from-[#0A0A18]/90 via-[#0A0A12]/85 to-[#0A0A18]/90 backdrop-blur-xl p-8 md:p-12 mb-12 shadow-[0_30px_80px_-30px_rgba(99,102,241,0.45)]"
            >
                {/* ambient glow orbs */}
                <div className="pointer-events-none absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-indigo-600/25 blur-[120px] glow-orb" />
                <div className="pointer-events-none absolute -bottom-40 -left-24 w-[420px] h-[420px] rounded-full bg-violet-600/20 blur-[120px] glow-orb" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent" />

                <div className="relative max-w-3xl">
                    <p className="text-[11px] tracking-[0.32em] uppercase text-indigo-300 mb-4 flex items-center gap-2">
                        <span className="inline-flex w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_12px_rgba(129,140,248,0.95)] trust-pulse" />
                        Fastest Response
                    </p>
                    <h2 className="text-3xl md:text-4xl font-light tracking-tight text-white">
                        Message us on <span className="text-gradient">Telegram.</span>
                    </h2>
                    <p className="mt-4 text-slate-300/85 leading-relaxed max-w-2xl">
                        For product inquiries, availability, or support, message us directly on Telegram for the quickest response.
                    </p>

                    <div className="mt-8">
                        <a
                            href="https://t.me/QuantumGenesisLabs"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="open-telegram-chat-button"
                            className="
                                group relative inline-flex items-center gap-3
                                rounded-full px-7 md:px-8 py-4
                                bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500
                                text-white font-medium tracking-tight
                                shadow-[0_18px_60px_-18px_rgba(99,102,241,0.85)]
                                transition-all duration-300 ease-out
                                hover:shadow-[0_22px_72px_-12px_rgba(129,140,248,0.95)]
                                hover:-translate-y-0.5 active:translate-y-0
                                focus:outline-none focus:ring-2 focus:ring-indigo-400/60 focus:ring-offset-2 focus:ring-offset-[#05050A]
                            "
                        >
                            {/* gradient ring */}
                            <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-white/25" />
                            <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-white/15 backdrop-blur-sm">
                                <Send className="w-3.5 h-3.5 text-white -translate-x-[1px] translate-y-[1px]" strokeWidth={2.4} />
                            </span>
                            <span className="relative text-sm md:text-base tracking-[0.08em] uppercase">
                                Open Telegram Chat
                            </span>
                            {/* shimmer */}
                            <span className="pointer-events-none absolute inset-0 rounded-full overflow-hidden">
                                <span className="absolute -inset-y-2 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/35 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-[400%] transition-all duration-700 ease-out" />
                            </span>
                        </a>
                    </div>

                    {/* Feature pills */}
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                        {[
                            { Icon: Zap, label: "Instant Replies", hint: "Usually under an hour." },
                            { Icon: ShieldCheck, label: "Secure Chat", hint: "End-to-end private." },
                            { Icon: MessageCircle, label: "Fast Support", hint: "Real humans, no bots." },
                        ].map(({ Icon, label, hint }, i) => (
                            <div
                                key={label}
                                data-testid={`telegram-feature-${i}`}
                                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A0A12]/70 backdrop-blur-md px-4 py-3 transition-all duration-300 hover:border-indigo-400/40 hover:bg-[#0F0F1A]/80"
                            >
                                <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/25 to-violet-600/20 border border-indigo-400/25 shadow-[0_0_18px_rgba(99,102,241,0.25)] shrink-0">
                                    <Icon className="w-4 h-4 text-indigo-200" strokeWidth={1.8} />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[11px] tracking-[0.2em] uppercase text-white leading-tight">{label}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{hint}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Divider: "Prefer email?" ──────────────────────────────────── */}
            <div className="flex items-center gap-4 mb-8 max-w-3xl mx-auto">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <p className="text-xs text-slate-500 tracking-[0.18em] uppercase whitespace-nowrap">
                    Prefer email? Send a detailed inquiry below
                </p>
                <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>

            {/* ─── SECONDARY: Inquiry form (unchanged functionality) ────────── */}
            <div className="max-w-3xl mx-auto">
                <form
                    onSubmit={onSubmit}
                    className="rounded-2xl border border-white/10 bg-[#0A0A12]/60 p-7 md:p-9 space-y-5"
                    data-testid="contact-form"
                >
                    {/* Honeypot — invisible to humans, irresistible to bots. */}
                    <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2 block">Name</label>
                            <Input
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder="Full Name"
                                data-testid="contact-name-input"
                                className="bg-white/[0.03] border-white/10 text-white h-11"
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2 block">Email</label>
                            <Input
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder="your@email.com"
                                data-testid="contact-email-input"
                                className="bg-white/[0.03] border-white/10 text-white h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2 block">Subject</label>
                        <Input
                            name="subject"
                            value={form.subject}
                            onChange={handleChange}
                            placeholder="Product Inquiry"
                            data-testid="contact-subject-input"
                            className="bg-white/[0.03] border-white/10 text-white h-11"
                        />
                    </div>

                    <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-slate-500 mb-2 block">Message</label>
                        <Textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="Please include the products and quantities you are interested in."
                            data-testid="contact-message-input"
                            className="bg-white/[0.03] border-white/10 text-white min-h-[140px]"
                        />
                    </div>

                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-button"
                        className="w-full md:w-auto rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-11 px-7 btn-glow"
                    >
                        <Send className="w-4 h-4 mr-2" />
                        {submitting ? "Sending..." : "Submit Inquiry"}
                    </Button>

                    {success && (
                        <p className="text-sm text-emerald-400" data-testid="contact-success-message">
                            Thanks — we'll be in touch shortly.
                        </p>
                    )}
                </form>

                {/* Research disclaimer / reminder */}
                <div className="mt-8 p-5 rounded-2xl border border-indigo-500/20 bg-indigo-500/5" data-testid="contact-reminder">
                    <p className="text-xs tracking-[0.2em] uppercase text-indigo-400 mb-2">Reminder</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                        All products are intended for laboratory and research purposes only.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Contact;
